<template>
    <div class="form-container">
        <h2>{{ isEditing ? 'Edit' : 'Add' }} Phase Determination</h2>
        <hr />

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div v-if="client">
            <p><strong>Client: </strong> {{ client.user?.name || "Unknown" }}</p>
            <p><strong>Presentive/Methasoft ID: </strong> {{ client.pid || 'Unknown' }}</p>
            <p><strong>Dose: </strong> {{ client.dose + " mg" }}</p>
            <p><strong>Admission Date: </strong> {{ $formatDate(client.admission_date) }} </p>
        </div>

        <div v-if="client_detail_error" class="alert alert-danger">
            {{ client_detail_error }}
        </div>

        <form v-if="!client_detail_error" @submit.prevent="submitForm">

            <input type="hidden" v-model="form.student_id" />

            <label>Dose *</label>
            <input v-model="form.dose" class="form-control" required>

            <div v-if="errors.dose" class="error-message">{{ errors.dose[0] }}</div>


            <label>Form Date *</label>
            <input v-model="form.form_date" type="date" required />
            <div v-if="errors.form_date" class="error-message">{{ errors.form_date[0] }}</div>

            <label>Time in Treatment (days) : <strong>{{ form.time_in_treatment_days }}</strong></label>
            <label>Current Phase *</label>
            <select v-model="form.current_phase" required>
                <option disabled value="">-- Select Phase --</option>
                <option v-for="phase in phaseOptions" :key="'current-' + phase.value" :value="phase.value">
                    {{ phase.label }}
                </option>
            </select>
            <div v-if="errors.current_phase" class="error-message">{{ errors.current_phase[0] }}</div>


            <label>Current Schedule *</label>
            <div class="checkbox-row">
                <div class="checkbox-item" v-for="day in scheduleOptions" :key="'curr-' + day">
                    <input type="checkbox" :id="'curr-' + day" :value="day" v-model="form.current_schedule" />
                    <label :for="'curr-' + day">{{ day }}</label>
                </div>
            </div>
            <div v-if="errors.current_schedule" class="error-message">{{ errors.current_schedule[0] }}</div>

            <label>Requested Phase *</label>
            <select v-model="form.requested_phase" required>
                <option disabled value="">-- Select Phase --</option>
                <option v-for="phase in phaseOptions" :key="'requested-' + phase.value" :value="phase.value">
                    {{ phase.label }}
                </option>
            </select>
            <div v-if="errors.requested_phase" class="error-message">{{ errors.requested_phase[0] }}</div>

            <label>Requested Schedule *</label>
            <div class="checkbox-row">
                <div class="checkbox-item" v-for="day in scheduleOptions" :key="'req-' + day">
                    <input type="checkbox" :id="'req-' + day" :value="day" v-model="form.requested_schedule" />
                    <label :for="'req-' + day">{{ day }}</label>
                </div>
            </div>
            <div v-if="errors.requested_schedule" class="error-message">{{ errors.requested_schedule[0] }}</div>




            <div class="btn-group mt-4">
                <button type="submit" class="btn btn-sm btn-success">{{ isEditing ? 'Update' : 'Add' }}</button>
                <button type="button" class="btn btn-sm cancel-btn"
                    @click="$router.push({ name: 'PhaseDeterminationDetail', params: { student_id: form.student_id } })">Cancel</button>
            </div>
        </form>
    </div>
</template>

<script>
export default {
    props: ['id'],
    data() {
        return {
            form: {
                student_id: '',
                dose: '',
                admit_date: '',
                form_date: '',
                time_in_treatment_days: '0',
                has_two_nonreactive_screens: false,
                current_phase: '',
                current_schedule: [],
                requested_phase: '',
                requested_schedule: [],
                counselor_impression: '',
                errorMessage: '',
                client_detail_error: '',
                showForm: true,
            },
            errors: {},
            isEditing: false,
            errorMessage: null,
            client: null,
            scheduleOptions: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            phaseOptions: [
                { label: 'Phase 0', value: 0 },
                { label: 'Phase 1', value: 1 },
                { label: 'Phase 2', value: 2 },
                { label: 'Phase 3', value: 3 },
                { label: 'Phase 4', value: 4 }
            ],
        };
    },
    async mounted() {
        const studentId = this.$route.query.student_id;

        if (studentId) {
            this.form.student_id = studentId;
            await this.fetchClientDetails(studentId);
        }

        if (this.id) {
            this.isEditing = true;
            await this.fetchFormData();
        }
    },
    watch: {
        'form.form_date'(newDate) {
            if (newDate && this.form.admit_date) {
                const admit = new Date(this.form.admit_date);
                const current = new Date(newDate);
                const diffTime = current - admit;
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                this.form.time_in_treatment_days = diffDays >= 0 ? diffDays : 0;
            } else {
                this.form.time_in_treatment_days = null;
            }
        },
        showForm(newval) {
            console.info('New form value changed ' + newval);
        }
    },
    methods: {
        async fetchClientDetails(id) {
            try {
                const res = await this.$api.get(`/students/${id}`);
                this.client = res.data;
                this.form.dose = this.client.dose;
                this.form.admit_date = this.client.admission_date;
                if (!this.client.dose || !this.client.admission_date) {
                    this.client_detail_error = 'Client details are incomplete. Please fill in Dose and Admit Date first.';
                    this.showForm = false;


                }
            } catch (err) {
                this.errorMessage = 'Failed to load client details.';
            }
        },
        async fetchFormData() {
            try {
                const res = await this.$api.get(`/phase-determinations/${this.id}`);
                const data = res.data;
                this.form = {
                    ...data,
                    current_schedule: data.current_schedule || [],
                    requested_schedule: data.requested_schedule || [],
                };

            } catch {
                this.errorMessage = 'Error loading phase data.';
            }
        },
        async submitForm() {
            this.errors = {};
            this.errorMessage = null;
            try {
                const payload = { ...this.form };
                if (this.$route.query.updateOnly) {
                    payload.updateOnly = this.$route.query.updateOnly;
                }
                if (this.isEditing) {
                    await this.$api.put(`/phase-determinations/${this.id}`, payload);
                } else {
                    await this.$api.post(`/phase-determinations`, payload);
                }

                this.$router.push({
                    name: 'PhaseDeterminationDetail',
                    params: { student_id: this.form.student_id }
                });

            } catch (err) {
                if (err.response?.status === 422) {
                    this.errors = err.response.data.errors;
                } else {
                    this.errorMessage = 'Failed to save. Please check your input or try again later.';
                }
            }
        }
    }
};
</script>


<style scoped>
.form-container {
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-container {
    max-width: 800px;
    /* was probably 400-600px */
    margin: 0 auto;
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

label {
    display: block;
    margin-top: 15px;
    font-weight: bold;
}

input,
select,
textarea {
    width: 100%;
    padding: 8px;
    margin-top: 5px;
    box-sizing: border-box;
}

textarea {
    resize: vertical;
}

.error-message {
    color: red;
    font-size: 0.9em;
    margin-top: 4px;
}

.btn-group {
    display: flex;
    gap: 10px;
    margin-top: 20px;
}

button {
    min-width: 150px;
}

.checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-top: 8px;
}

.checkbox-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 8px;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 130px;
}

.checkbox-item label {
    margin-top: 0px;
    padding-top: 0px;
    line-height: 31px;
}

.form-check {
    min-width: 120px;
}
</style>